// Maps a route path to the org-role permission required to OPEN it. ProtectedRoute
// consults this so page/route access is permission-authoritative — an admin
// toggling a role's permission controls the page itself, not just the sidebar tab
// and the data (RLS). When a path is mapped, the permission is the gate and the
// route's allowedRoles is ignored (so custom roles work). Paths NOT listed fall
// back to their allowedRoles (platform/utility routes: super-admin, agency,
// affiliate, crew-dashboard, quickbooks, marketing sub-features, payments, team,
// dashboard, etc.).
//
// Match is segment-aware longest-prefix: '/jobs/scheduling' wins over '/jobs', and
// '/mockups-lite' does NOT match '/mockups'.

interface RoutePermission {
  prefix: string;
  permission: string;
}

const ROUTE_PERMISSIONS: RoutePermission[] = [
  { prefix: '/jobs/scheduling', permission: 'tab.scheduling' },
  { prefix: '/jobs', permission: 'tab.jobs' },
  { prefix: '/activity', permission: 'tab.activity' },
  { prefix: '/pipeline', permission: 'tab.pipeline' },
  { prefix: '/crm', permission: 'tab.clients' },
  { prefix: '/proposals', permission: 'tab.proposals' },
  { prefix: '/estimates', permission: 'tab.proposals' },
  { prefix: '/quotes', permission: 'tab.proposals' },
  { prefix: '/service-requests', permission: 'tab.service_requests' },
  { prefix: '/inventory', permission: 'tab.inventory' },
  { prefix: '/routes', permission: 'tab.routes' },
  { prefix: '/invoices', permission: 'tab.invoices' },
  { prefix: '/measurements', permission: 'tab.measurements' },
  { prefix: '/mockups-lite', permission: 'tab.mockups_lite' },
  { prefix: '/mockups', permission: 'tab.mockups' },
  { prefix: '/messages', permission: 'tab.messages' },
  { prefix: '/marketing/doordrop', permission: 'tab.doordrop' },
  { prefix: '/reputation', permission: 'tab.reputation' },
  { prefix: '/expenses', permission: 'tab.expenses' },
  { prefix: '/reports', permission: 'tab.reports' },
  { prefix: '/support', permission: 'tab.support' },
  { prefix: '/settings', permission: 'tab.settings' },
];

// Priority order for choosing a fallback landing page when a user can't open the
// dashboard (e.g. a custom role that wasn't granted tab.dashboard). First tab the
// user actually has permission for wins, so they never dead-end on /forbidden.
const LANDING_PRIORITY: RoutePermission[] = [
  { prefix: '/jobs', permission: 'tab.jobs' },
  { prefix: '/pipeline', permission: 'tab.pipeline' },
  { prefix: '/crm', permission: 'tab.clients' },
  { prefix: '/proposals', permission: 'tab.proposals' },
  { prefix: '/invoices', permission: 'tab.invoices' },
  { prefix: '/service-requests', permission: 'tab.service_requests' },
  { prefix: '/measurements', permission: 'tab.measurements' },
  { prefix: '/mockups', permission: 'tab.mockups' },
  { prefix: '/inventory', permission: 'tab.inventory' },
  { prefix: '/routes', permission: 'tab.routes' },
  { prefix: '/messages', permission: 'tab.messages' },
  { prefix: '/reputation', permission: 'tab.reputation' },
  { prefix: '/expenses', permission: 'tab.expenses' },
  { prefix: '/reports', permission: 'tab.reports' },
  { prefix: '/activity', permission: 'tab.activity' },
  { prefix: '/settings', permission: 'tab.settings' },
];

/** First route the user can open, in landing-priority order; null if none. */
export function firstAccessibleRoute(can: (permission: string) => boolean): string | null {
  for (const r of LANDING_PRIORITY) {
    if (can(r.permission)) return r.prefix;
  }
  return null;
}

/** The permission required to open `pathname`, or null if the route isn't
 *  permission-mapped (caller should fall back to allowedRoles). */
export function permissionForPath(pathname: string): string | null {
  let best: RoutePermission | null = null;
  for (const r of ROUTE_PERMISSIONS) {
    const isMatch = pathname === r.prefix || pathname.startsWith(`${r.prefix}/`);
    if (isMatch && (!best || r.prefix.length > best.prefix.length)) {
      best = r;
    }
  }
  return best?.permission ?? null;
}
